/**
 * Admin Dashboard - Overview
 * Main landing page showing key metrics across sales, onboarding, and operations
 */

import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Text,
  VStack,
  HStack,
  Icon,
  Badge,
  Button,
  useColorModeValue,
  Flex,
  Spinner,
  Progress,
} from '@chakra-ui/react';
import {
  MdTrendingUp,
  MdAttachMoney,
  MdPeople,
  MdAssignment,
  MdSpeed,
  MdCheckCircle,
  MdWarning,
  MdTimeline,
  MdPhone,
  MdEmail,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getLeadStats } from '../../services/leadManagement';
import { 
  getRecentActivities, 
  getUpcomingTasks, 
  DashboardActivity, 
  DashboardTask 
} from '../../services/dashboardService';
import { LeadStats } from '../../types/lead';

export function Dashboard() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [leadStats, setLeadStats] = useState<LeadStats | null>(null);
  const [recentActivities, setRecentActivities] = useState<DashboardActivity[]>([]);
  const [upcomingTasks, setUpcomingTasks] = useState<DashboardTask[]>([]);
  const [loading, setLoading] = useState(true);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      // Load all dashboard data in parallel
      const [stats, activities, tasks] = await Promise.all([
        getLeadStats(),
        getRecentActivities(5),
        getUpcomingTasks(5),
      ]);
      setLeadStats(stats);
      setRecentActivities(activities);
      setUpcomingTasks(tasks);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box>
      <VStack spacing="20px" align="stretch">
        {/* Welcome Banner */}
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <CardBody>
            <HStack justify="space-between">
              <VStack align="start" spacing="2">
                <Heading size="lg">Welcome back, {userProfile?.name?.split(' ')[0] || 'Admin'}! 👋</Heading>
                <Text color="gray.500">
                  Here's what's happening with your business today.
                </Text>
              </VStack>
              <HStack spacing="4">
                <Button
                  leftIcon={<MdEmail />}
                  colorScheme="brand"
                  onClick={() => navigate('/admin/sales/leads/new')}
                >
                  New Lead
                </Button>
                <Button
                  leftIcon={<MdPhone />}
                  variant="outline"
                  onClick={() => navigate('/admin/sales/demos')}
                >
                  Schedule Demo
                </Button>
              </HStack>
            </HStack>
          </CardBody>
        </Card>

        {/* Key Metrics */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap="20px">
          {/* Pipeline Value */}
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardBody>
              <Stat>
                <HStack mb="2">
                  <Icon as={MdAttachMoney} boxSize="6" color="green.500" />
                  <StatLabel>Pipeline Value</StatLabel>
                </HStack>
                <StatNumber>{leadStats ? formatCurrency(leadStats.totalPipelineValue) : '$0'}</StatNumber>
                <StatHelpText>
                  <StatArrow type="increase" />
                  Weighted: {leadStats ? formatCurrency(leadStats.weightedPipelineValue) : '$0'}
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          {/* Active Leads */}
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardBody>
              <Stat>
                <HStack mb="2">
                  <Icon as={MdPeople} boxSize="6" color="blue.500" />
                  <StatLabel>Active Leads</StatLabel>
                </HStack>
                <StatNumber>{leadStats?.total || 0}</StatNumber>
                <StatHelpText>
                  Close rate: {leadStats?.conversionRates.overallCloseRate.toFixed(1) || '0'}%
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          {/* Avg Deal Size */}
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardBody>
              <Stat>
                <HStack mb="2">
                  <Icon as={MdTrendingUp} boxSize="6" color="purple.500" />
                  <StatLabel>Avg Deal Size</StatLabel>
                </HStack>
                <StatNumber>
                  {leadStats ? formatCurrency(leadStats.totalPipelineValue / (leadStats.total || 1)) : '$0'}
                </StatNumber>
                <StatHelpText>
                  Avg doors: {leadStats ? Math.round(leadStats.avgDoorCount) : 0}
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          {/* Lead Score */}
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardBody>
              <Stat>
                <HStack mb="2">
                  <Icon as={MdSpeed} boxSize="6" color="orange.500" />
                  <StatLabel>Avg Lead Score</StatLabel>
                </HStack>
                <StatNumber>{leadStats ? Math.round(leadStats.avgScore) : 0}/100</StatNumber>
                <StatHelpText>
                  <Badge colorScheme={leadStats && leadStats.avgScore >= 70 ? 'green' : 'yellow'}>
                    {leadStats && leadStats.avgScore >= 70 ? 'High Quality' : 'Medium Quality'}
                  </Badge>
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, lg: 2 }} gap="20px">
          {/* Sales Pipeline Status */}
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardHeader>
              <Heading size="md">
                <Icon as={MdTimeline} mr="2" />
                Pipeline Status
              </Heading>
            </CardHeader>
            <CardBody>
              {leadStats && (
                <VStack spacing="4" align="stretch">
                  <Box>
                    <HStack justify="space-between" mb="2">
                      <Text fontSize="sm" fontWeight="medium">New Leads</Text>
                      <Text fontSize="sm" fontWeight="bold">{leadStats.byStatus.new || 0}</Text>
                    </HStack>
                    <Progress
                      value={((leadStats.byStatus.new || 0) / leadStats.total) * 100}
                      colorScheme="blue"
                      size="sm"
                      borderRadius="full"
                    />
                  </Box>

                  <Box>
                    <HStack justify="space-between" mb="2">
                      <Text fontSize="sm" fontWeight="medium">Qualifying</Text>
                      <Text fontSize="sm" fontWeight="bold">{leadStats.byStatus.qualifying || 0}</Text>
                    </HStack>
                    <Progress
                      value={((leadStats.byStatus.qualifying || 0) / leadStats.total) * 100}
                      colorScheme="cyan"
                      size="sm"
                      borderRadius="full"
                    />
                  </Box>

                  <Box>
                    <HStack justify="space-between" mb="2">
                      <Text fontSize="sm" fontWeight="medium">Demo Scheduled</Text>
                      <Text fontSize="sm" fontWeight="bold">{leadStats.byStatus.demo_scheduled || 0}</Text>
                    </HStack>
                    <Progress
                      value={((leadStats.byStatus.demo_scheduled || 0) / leadStats.total) * 100}
                      colorScheme="purple"
                      size="sm"
                      borderRadius="full"
                    />
                  </Box>

                  <Box>
                    <HStack justify="space-between" mb="2">
                      <Text fontSize="sm" fontWeight="medium">Proposal Sent</Text>
                      <Text fontSize="sm" fontWeight="bold">{leadStats.byStatus.proposal_sent || 0}</Text>
                    </HStack>
                    <Progress
                      value={((leadStats.byStatus.proposal_sent || 0) / leadStats.total) * 100}
                      colorScheme="orange"
                      size="sm"
                      borderRadius="full"
                    />
                  </Box>

                  <Box>
                    <HStack justify="space-between" mb="2">
                      <Text fontSize="sm" fontWeight="medium">Closed Won</Text>
                      <Text fontSize="sm" fontWeight="bold" color="green.500">
                        {leadStats.byStatus.closed_won || 0}
                      </Text>
                    </HStack>
                    <Progress
                      value={((leadStats.byStatus.closed_won || 0) / leadStats.total) * 100}
                      colorScheme="green"
                      size="sm"
                      borderRadius="full"
                    />
                  </Box>
                </VStack>
              )}
            </CardBody>
          </Card>

          {/* Recent Activity */}
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardHeader>
              <Heading size="md">
                <Icon as={MdAssignment} mr="2" />
                Recent Activity
              </Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing="4" align="stretch">
                {recentActivities.length === 0 ? (
                  <Text fontSize="sm" color="gray.500" textAlign="center" py="4">
                    No recent activities. Start by adding leads or scheduling demos.
                  </Text>
                ) : (
                  recentActivities.map((activity) => (
                    <Box
                      key={activity.id}
                      p="4"
                      borderWidth="1px"
                      borderColor={borderColor}
                      borderRadius="md"
                      _hover={{ bg: useColorModeValue('gray.50', 'gray.700'), cursor: 'pointer' }}
                      onClick={() => {
                        if (activity.resourceType === 'lead' && activity.resourceId) {
                          navigate(`/admin/sales/leads/${activity.resourceId}`);
                        } else if (activity.resourceType === 'onboarding' && activity.resourceId) {
                          navigate(`/admin/onboarding/${activity.resourceId}`);
                        }
                      }}
                    >
                      <HStack justify="space-between" mb="2">
                        <HStack>
                          <Badge
                            colorScheme={
                              activity.type === 'demo' ? 'purple' :
                              activity.type === 'proposal' ? 'blue' :
                              activity.type === 'onboarding' ? 'green' :
                              activity.type === 'contract' ? 'teal' :
                              'gray'
                            }
                          >
                            {activity.type}
                          </Badge>
                          <Text fontWeight="bold" fontSize="sm">{activity.title}</Text>
                        </HStack>
                        {activity.priority === 'high' && (
                          <Icon as={MdWarning} color="orange.500" />
                        )}
                      </HStack>
                      <Text fontSize="sm" color="gray.500">{activity.description}</Text>
                      <HStack justify="space-between" mt="2">
                        <Text fontSize="xs" color="gray.400">
                          {activity.timestamp.toLocaleString([], { 
                            month: 'short', 
                            day: 'numeric', 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </Text>
                        {activity.user && (
                          <Text fontSize="xs" color="gray.400">by {activity.user}</Text>
                        )}
                      </HStack>
                    </Box>
                  ))
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/admin/sales/leads')}
                >
                  View All Activity →
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Upcoming Tasks & Onboarding */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} gap="20px">
          {/* Upcoming Tasks */}
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardHeader>
              <Heading size="md">
                <Icon as={MdCheckCircle} mr="2" />
                Upcoming Tasks
              </Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing="3" align="stretch">
                {upcomingTasks.length === 0 ? (
                  <Text fontSize="sm" color="gray.500" textAlign="center" py="4">
                    No upcoming tasks. Great job staying on top of things!
                  </Text>
                ) : (
                  upcomingTasks.map((task) => (
                    <Box
                      key={task.id}
                      p="3"
                      borderWidth="1px"
                      borderColor={borderColor}
                      borderRadius="md"
                      _hover={{ bg: useColorModeValue('gray.50', 'gray.700'), cursor: 'pointer' }}
                      onClick={() => {
                        if (task.resourceType === 'lead' && task.resourceId) {
                          navigate(`/admin/sales/leads/${task.resourceId}`);
                        } else if (task.resourceType === 'onboarding' && task.resourceId) {
                          navigate(`/admin/onboarding/${task.resourceId}`);
                        }
                      }}
                    >
                      <HStack justify="space-between">
                        <VStack align="start" spacing="1">
                          <Text fontWeight="medium" fontSize="sm">{task.title}</Text>
                          <Text fontSize="xs" color={task.dueIn === 'Overdue' ? 'red.500' : 'gray.500'}>
                            {task.dueIn === 'Overdue' ? '⚠️ Overdue' : `Due in ${task.dueIn}`}
                          </Text>
                        </VStack>
                        <Badge
                          colorScheme={
                            task.priority === 'high' ? 'red' : 
                            task.priority === 'medium' ? 'yellow' : 
                            'green'
                          }
                        >
                          {task.priority}
                        </Badge>
                      </HStack>
                    </Box>
                  ))
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/admin/onboarding')}
                >
                  View All Tasks →
                </Button>
              </VStack>
            </CardBody>
          </Card>

          {/* Quick Actions */}
          <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
            <CardHeader>
              <Heading size="md">Quick Actions</Heading>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={2} spacing="3">
                <Button
                  h="80px"
                  flexDirection="column"
                  onClick={() => navigate('/admin/sales/leads/new')}
                  colorScheme="brand"
                  variant="outline"
                >
                  <Icon as={MdPeople} boxSize="6" mb="2" />
                  <Text fontSize="sm">Add Lead</Text>
                </Button>

                <Button
                  h="80px"
                  flexDirection="column"
                  onClick={() => navigate('/admin/sales/demos')}
                  colorScheme="purple"
                  variant="outline"
                >
                  <Icon as={MdPhone} boxSize="6" mb="2" />
                  <Text fontSize="sm">Book Demo</Text>
                </Button>

                <Button
                  h="80px"
                  flexDirection="column"
                  onClick={() => navigate('/admin/sales/proposals/new')}
                  colorScheme="blue"
                  variant="outline"
                >
                  <Icon as={MdEmail} boxSize="6" mb="2" />
                  <Text fontSize="sm">Send Proposal</Text>
                </Button>

                <Button
                  h="80px"
                  flexDirection="column"
                  onClick={() => navigate('/admin/onboarding')}
                  colorScheme="green"
                  variant="outline"
                >
                  <Icon as={MdAssignment} boxSize="6" mb="2" />
                  <Text fontSize="sm">Onboarding</Text>
                </Button>
              </SimpleGrid>
            </CardBody>
          </Card>
        </SimpleGrid>
      </VStack>
    </Box>
  );
}

export default Dashboard;
